import React, { useCallback, useEffect, useRef, useState } from 'react'
import { t } from 'src/translations'
import { AllergenWithSource } from './useAllergensWithSource'
import FocusTrap from 'focus-trap-react'
import {
  ModalCloseButton,
  ModalContext,
  ModalOverlay
} from 'shared/components/common/modal'
import { Dialog } from 'react-aria-components'

type AllergenInfoSheetProps = {
  isOpen: boolean
  onClose: () => void
  allergens: AllergenWithSource[]
}

export const AllergenInfoSheet = ({
  isOpen,
  onClose,
  allergens
}: AllergenInfoSheetProps) => {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const sheetRef = useRef<HTMLElement>(null)
  const animationTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (!isOpen) {
      setShouldAnimate(false)
      return
    }
    let frame2: number | undefined
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        setShouldAnimate(true)
      })
    })

    return () => {
      cancelAnimationFrame(frame1)
      if (frame2 !== undefined) cancelAnimationFrame(frame2)
    }
  }, [isOpen])

  useEffect(() => () => clearTimeout(animationTimerRef.current), [])

  const runAnimation = useCallback((cb: () => void) => {
    clearTimeout(animationTimerRef.current)
    const element = sheetRef.current
    element?.classList.add('slideDown')

    animationTimerRef.current = setTimeout(() => {
      element?.classList.remove('slideDown')
      cb()
    }, 300)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        runAnimation(onClose)
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, runAnimation, onClose])

  if (!isOpen) return null

  return (
    <ModalContext.Provider value={{ onClose: () => runAnimation(onClose) }}>
      <ModalOverlay opacity={65} fadeIn fadeOut duration={0.3} />
      <FocusTrap focusTrapOptions={{ allowOutsideClick: true }}>
        <Dialog
          ref={sheetRef}
          className={`secondaryContent allergenSheet${
            shouldAnimate ? ' slideUp' : ''
          }`}
          aria-labelledby='allergen-sheet-title'
        >
          <ModalCloseButton
            className='itemModalCloseButton'
            onClose={() => runAnimation(onClose)}
          />
          <h2 id='allergen-sheet-title' className='pinnedHeader pinned'>
            <span className='headerText secondary'>{t('allergens.title')}</span>
          </h2>
          <div className='contentBody'>
            <div className='allergenSheetContent'>
              <table className='allergenTable'>
                <thead>
                  <tr>
                    <th>{t('allergen-info.column-allergen')}</th>
                    <th>{t('allergen-info.column-source')}</th>
                  </tr>
                </thead>
                <tbody>
                  {allergens.map(({ allergen, source }) => (
                    <tr key={allergen.guid}>
                      <td>{t(`allergens.${allergen.code}`, allergen.code)}</td>
                      <td>{source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>{t('allergen-info.disclaimer')}</p>
            </div>
          </div>
        </Dialog>
      </FocusTrap>
    </ModalContext.Provider>
  )
}
